import axios from "axios";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import IdentityVerificationStep from "./IdentityVerificationStep";
import InvestmentLimitsStep from "./InvestmentLimitsStep";
import OTPVerificationStep from "./OTPVerificationStep";
import PhoneVerificationStep from "./PhoneVerificationStep";
import WelcomeStep from "./WelcomeStep";
import useConfig from "../../hooks/useConfig";
import { loadPublicConfigs } from "../../redux/slices/configSlice";
import Footer from "../../siteComponents/Footer";
import TopBar from "../../siteComponents/Topbar";

import { notifyUser } from "../../utils/notifyUser";

function MultiStepAccount({ user, setUser }) {
    const effectiveUser = user;
    const effectiveEmail = effectiveUser?.email;

    const [currentStep, setCurrentStep] = useState(2);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [readyToNavigate, setReadyToNavigate] = useState(false);
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: effectiveEmail,
        phone: "",
        birthdate: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipCode: "",
        linkedinUrl: "",
        ssn: "",
        isAccreditedInvestor: "",
        accreditedInvestorType: "",
        netWorth: "",
        yearlyIncome: "",
        priorRegCfAmount: "0",
    });

    // Phone verification states
    const [otpSent, setOtpSent] = useState(false);
    const [otp, setOtp] = useState("");
    const [otpVerified, setOtpVerified] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);

    // Ref to prevent concurrent verification attempts
    const verificationInProgressRef = useRef(false);

    // Terms acceptance
    const [termsAccepted, setTermsAccepted] = useState(false);

    const { getConfig, loading: configLoading } = useConfig();
    const phoneVerificationEnabled =
        getConfig("ENABLE_PHONE_VERIFICATION") === "true";
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Load public configs for account creation (when user might not be authenticated)
    useEffect(() => {
        dispatch(loadPublicConfigs());
    }, [dispatch]);

    // Navigate to dashboard after user state is confirmed updated
    useEffect(() => {
        if(readyToNavigate && user?.application_submitted === 1) {
            navigate("/dashboard");
        }
    }, [readyToNavigate, user, navigate]);

    // Redirect check
    useEffect(() => {
        const currentUser =
            user || JSON.parse(localStorage.getItem("user") || "{}") || {};
        if(window.location.pathname.includes("dashboard")) {
            if(
                currentUser.role === "investor" &&
                String(currentUser.application_submitted) === "0"
            ) {
                navigate("/account-creation");
            }
        }
    }, [user, navigate]);

    // Prefill data from backend
    useEffect(() => {
        const email = effectiveEmail;
        if(!email) return;

        const fetchPrefillData = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/investor-info`,
                    {
                        params: { email },
                    },
                );

                const data = response.data;

                if(data.firstName && data.firstName.trim()) {
                    setFormData((prev) => ({
                        ...prev,
                        firstName: data.firstName,
                    }));
                } else if(
                    (!data.firstName || !data.firstName.trim()) &&
                    data.signature_text &&
                    data.signature_text.trim()
                ) {
                    const parts = data.signature_text.trim().split(" ");
                    if(parts.length === 1) {
                        setFormData((prev) => ({
                            ...prev,
                            firstName: parts[0],
                        }));
                    } else if(parts.length > 1) {
                        setFormData((prev) => ({
                            ...prev,
                            firstName: parts[0],
                        }));
                    }
                }

                if(data.lastName && data.lastName.trim()) {
                    setFormData((prev) => ({
                        ...prev,
                        lastName: data.lastName,
                    }));
                } else if(
                    (!data.lastName || !data.lastName.trim()) &&
                    data.signature_text &&
                    data.signature_text.trim()
                ) {
                    const parts = data.signature_text.trim().split(" ");
                    if(parts.length > 1) {
                        setFormData((prev) => ({
                            ...prev,
                            lastName: parts.slice(1).join(" "),
                        }));
                    }
                }

                setFormData((prev) => ({
                    ...prev,
                    email: data.email || effectiveEmail,
                    phone: data.phone_number || "",
                    address1: data.address1 || "",
                    address2: data.address2 || "",
                    city: data.city || "",
                    state: data.state || "",
                    zipCode: data.zipcode || "",
                    linkedinUrl: data.linkedin || "",
                    birthdate: data.birthdate
                        ? data.birthdate.split("T")[0]
                        : "",
                    ssn: data.ssn_masked || "",
                    isAccreditedInvestor:
                        data.is_accredited_investor === "Yes"
                            ? "true"
                            : data.is_accredited_investor === "No"
                              ? "false"
                              : data.is_accredited_investor || "",
                    accreditedInvestorType: data.accredited_investor_type || "",
                    netWorth: data.net_worth || "",
                    yearlyIncome: data.yearly_income || "",
                    priorRegCfAmount: data.prior_reg_cf_amount || "",
                }));

                // Check if already submitted
                if(
                    (data.status === "pending" || data.status === "approved") &&
                    data.submitted
                ) {
                    setIsSubmitted(true);
                }
            } catch(error) {
                console.error("Error fetching prefill data:", error);
            }
        };

        fetchPrefillData();
    }, [effectiveEmail, configLoading]);

    // Phone verification functions
    const handleSendOtp = async () => {
        if(!phoneVerificationEnabled) {
            setOtpVerified(true);
            notifyUser(null, "success", "Phone verification is disabled. Your phone number has been accepted.", "toast", null, null, null, null, null, false, false, "top-center");
            return true;
        }

        if(!formData.phone) {
            notifyUser(null, "error", "Please enter a phone number.", "toast", null, null, null, null, null, false, false, "top-center");
            return false;
        }

        setOtpLoading(true);
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/send-otp`,
                {
                    phoneNumber: formData.phone,
                    email: formData.email,
                },
            );

            if(res.data.disabled || res.data.verified) {
                setOtpVerified(true);
                notifyUser(null, "success", "Phone verification is disabled. Your phone number has been accepted.", "toast", null, null, null, null, null, false, false, "top-center");
                return true;
            } else {
                setOtpSent(true);
                notifyUser(null, "success", "OTP sent to your phone number via SMS.", "toast", null, null, null, null, null, false, false, "top-center");
                return true;
            }
        } catch(err) {
            console.error("Send OTP failed:", err);

            if(err.response?.data?.alreadyInUse) {
                notifyUser(null, "error", "Phone number is already in use by another account. Please use a different number.", "toast", null, null, null, null, null, false, false, "top-center");
                return false;
            } else if(
                err.response?.data?.error ===
                "Twilio credentials not configured"
            ) {
                // For development: Show friendly message about SMS service being in development mode
                notifyUser(null, "warning", "SMS service is running in development mode. Use OTP code: 123456 for testing.", "toast", null, null, null, null, null, false, false, "top-center");
                setOtpSent(true);
                // Also log to console for developers
                console.log(
                    "🚀 DEVELOPMENT MODE: Use OTP code 123456 to verify your phone number",
                );
                return true;
            } else if(err.response?.status === 500) {
                notifyUser(null, "error", "SMS service temporarily unavailable. Please try again later or contact support.", "toast", null, null, null, null, null, false, false, "top-center");
                return false;
            } else {
                notifyUser(null, "error", err.response?.data?.message ||
                        "Failed to send OTP. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
                return false;
            }
        } finally {
            setOtpLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        // Prevent multiple concurrent verification attempts
        if(otpLoading || verificationInProgressRef.current) {
            return false;
        }

        if(!phoneVerificationEnabled) {
            setOtpVerified(true);
            notifyUser(null, "success", "Phone verification is disabled. Your phone number has been accepted.", "toast", null, null, null, null, null, false, false, "top-center");
            return true;
        }

        if(!otp || otp.length !== 6) {
            notifyUser(null, "error", "Please enter the complete 6-digit verification code.", "toast", null, null, null, null, null, false, false, "top-center");
            return false;
        }

        // If already verified, don't verify again
        if(otpVerified) {
            return true;
        }

        // Set both state and ref to prevent concurrent calls
        setOtpLoading(true);
        verificationInProgressRef.current = true;
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/verify-otp`,
                {
                    phoneNumber: formData.phone,
                    otp,
                    email: formData.email,
                },
            );

            if(res.data.disabled) {
                setOtpVerified(true);
                console.log(
                    "Phone verification disabled - marking as verified",
                );
                notifyUser(null, "success", "Phone verification is disabled. Your phone number has been accepted.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                setOtpVerified(true);
                console.log(
                    "Phone verification successful - marking as verified",
                );
                notifyUser(null, "success", "Phone number verified successfully!", "toast", null, null, null, null, null, false, false, "top-center");
            }
            console.log("handleVerifyOtp returning true");
            return true;
        } catch(err) {
            console.error("OTP verification failed:", err);
            notifyUser(null, "error", err.response?.data?.message ||
                    "Invalid or expired verification code. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
            return false;
        } finally {
            setOtpLoading(false);
            verificationInProgressRef.current = false;
        }
    };

    // Navigation functions
    const getTotalSteps = () => {
        return 5;
    };

    const getEffectiveStep = () => {
        // Map internal steps to display as "Step X of 5" where we start from step 2 internally
        // Step 2 (IdentityVerificationStep) = Display as Step 3 of 5
        // Step 3 (PhoneVerificationStep) = Display as Step 4 of 5
        // Step 5 (InvestmentLimitsStep) = Display as Step 5 of 5
        const stepMapping = {
            2: 3,
            3: 4,
            5: 5,
        };

        return stepMapping[currentStep] || currentStep;
    };

    const nextStep = () => {
        console.log(
            "NextStep called, current step:",
            currentStep,
            "Phone verification enabled:",
            phoneVerificationEnabled,
        );

        // Handle step transitions starting from step 2 (IdentityVerificationStep)
        if(currentStep === 2) {
            // From step 2 (Identity) go to step 3 (Phone)
            setCurrentStep(3);
        } else if(currentStep === 3) {
            // From step 3 (Phone) go to step 5 (Investment) - skip OTP step
            setCurrentStep(5);
        } else {
            setCurrentStep((prev) => {
                const newStep = prev + 1;
                console.log("Moving from step", prev, "to step", newStep);
                return newStep;
            });
        }
    };

    const prevStep = () => {
        if(currentStep === 5) {
            // Go back from investment (5) to phone (3)
            console.log("Going back from investment to phone step");
            setCurrentStep(3);
        } else if(currentStep === 3) {
            // Go back from phone (3) to identity (2)
            console.log("Going back from phone to identity step");
            setCurrentStep(2);
        } else {
            setCurrentStep((prev) => prev - 1);
        }
    };

    // Final submission
    const handleFinalSubmit = async () => {
        // Validate phone verification if enabled
        if(!validatePhoneVerification()) {
            return;
        }

        setIsSubmitting(true);
        const localUser = JSON.parse(localStorage.getItem("user") || "{}");

        // Debug user data
        console.log("User from props:", user);
        console.log("User from localStorage:", localUser);

        // Try multiple ways to get the user ID
        const userId =
            user?.id || user?.user_id || localUser?.id || localUser?.user_id;
        console.log("Extracted userId:", userId);

        if(!userId) {
            notifyUser(null, "error", "User ID not found. Please log in again.", "toast", null, null, null, null, null, false, false, "top-center");
            setIsSubmitting(false);
            return;
        }

        try {
            const requestData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                address1: formData.address1,
                address2: formData.address2,
                city: formData.city,
                state: formData.state,
                birthdate: formData.birthdate,
                zipCode: formData.zipCode,
                linkedin: formData.linkedinUrl,
                userId: userId,
                ssn: formData.ssn,
                isAccreditedInvestor: formData.isAccreditedInvestor,
                accreditedInvestorType: formData.accreditedInvestorType || null,
                netWorth: formData.netWorth || null,
                yearlyIncome: formData.yearlyIncome || null,
                priorRegCfAmount: formData.priorRegCfAmount || 0,
            };

            console.log("Request data being sent:", requestData);
            console.log("FormData state:", formData);

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/account-creation`,
                requestData,
            );

            if(response.status === 200) {
                notifyUser(null, "success", <div>
                        <h3 className="text-lg font-semibold">You're All Set!</h3>
                        <p className="text-sm mt-1">Thank you for completing your investor application! You will now be redirecting to your investor dashboard to explore active campaigns and start investing.</p>
                    </div>, "toast", null, null, 8000, null, null, false, false, "top-center");
                setIsSubmitted(true);

                setTimeout(() => {
                    // Build updatedUser from the current user prop (which has id and all fields)
                    const updatedUser = {
                        ...(user || {}),
                        role: "investor",
                        submitted: true,
                        application_submitted: 1,
                        isProfileVerified: "true",
                    };
                    localStorage.setItem("user", JSON.stringify(updatedUser));

                    if(setUser) {
                        setUser((prev) => ({
                            ...prev,
                            role: "investor",
                            submitted: true,
                            application_submitted: 1,
                            isProfileVerified: "true",
                        }));
                    }

                    // Dispatch custom event
                    const userUpdatedEvent = new CustomEvent("user-logged-in", {
                        detail: updatedUser,
                    });
                    window.dispatchEvent(userUpdatedEvent);

                    // Signal to navigate — actual navigation happens in useEffect
                    // once the user prop has been updated with application_submitted=1
                    setReadyToNavigate(true);
                }, 3000);
            } else {
                notifyUser(null, "error", "Failed to continue.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(error) {
            console.error("Form submission error:", error);
            if(error.response) {
                const errorData = error.response.data;
                if(error.response.status === 400 && errorData.missingFields) {
                    console.error("Missing fields:", errorData.missingFields);
                    notifyUser(null, "error", `Please fill in all required fields: ${errorData.missingFields.join(
                            ", ",
                        )}`, "toast", null, null, null, null, null, false, false, "top-center");
                } else {
                    const errorMessage =
                        errorData?.message ||
                        errorData?.error ||
                        "Failed to submit form.";
                    notifyUser(null, "error", errorMessage, "toast", null, null, null, null, null, false, false, "top-center");
                }
            } else if(error.request) {
                notifyUser(null, "error", "Network error. Please try submitting again.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                notifyUser(null, "error", "An unexpected error occurred.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Resend OTP function
    const handleResendOtp = async () => {
        if(!phoneVerificationEnabled) {
            setOtpVerified(true);
            notifyUser(null, "success", "Phone verification is disabled. Your phone number has been accepted.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setOtp("");

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/send-otp`,
                {
                    phoneNumber: formData.phone,
                    email: formData.email,
                },
            );

            if(res.data.disabled || res.data.verified) {
                setOtpVerified(true);
                notifyUser(null, "success", "Phone verification is disabled. Your phone number has been accepted.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                setOtpSent(true);
                notifyUser(null, "success", "New verification code sent to your phone.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(err) {
            console.error("Resend OTP failed:", err);
            notifyUser(null, "error", "Failed to resend verification code. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };
    useEffect(() => {
        document.title = "Investor Account Creation | AQI";
    }, []);

    // Reset phone verification when phone number changes
    const handlePhoneChange = (newPhone) => {
        updateFormData({ phone: newPhone });

        // Reset verification status when phone changes (if verification is enabled)
        if(phoneVerificationEnabled && formData.phone !== newPhone) {
            setOtpVerified(false);
            setOtpSent(false);
            setOtp("");
        }
    };

    // Check phone verification status on phone change
    useEffect(() => {
        if(formData.phone && !configLoading) {
            // Reset verification state when phone changes
            if(phoneVerificationEnabled) {
                checkPhoneVerification(formData.phone);
            } else {
                // Skip verification if disabled
                setOtpVerified(true);
            }
        }
    }, [formData.phone, phoneVerificationEnabled, configLoading]);

    // Check phone verification status
    const checkPhoneVerification = async (phoneNumber) => {
        if(!phoneNumber || configLoading) return;

        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/check-verification/${phoneNumber}`,
                {
                    params: { email: formData.email },
                },
            );

            if(res.data.verified || res.data.disabled) {
                setOtpVerified(true);
                if(res.data.disabled) {
                    notifyUser(null, "success", "Phone verification is disabled. Your phone number has been accepted.", "toast", null, null, null, null, null, false, false, "top-center");
                }
            } else if(res.data.alreadyInUse) {
                notifyUser(null, "error", "Phone number is already in use by another account. Please use a different number.", "toast", null, null, null, null, null, false, false, "top-center");
                setOtpVerified(false);
            } else {
                setOtpVerified(false);
            }
        } catch(err) {
            if(err.response?.data?.alreadyInUse) {
                notifyUser(null, "error", "Phone number is already in use by another account. Please use a different number.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                console.error(
                    "Failed to check phone verification status:",
                    err,
                );
            }
            setOtpVerified(false);
        }
    };

    // Update form data
    const updateFormData = (newData) => {
        setFormData((prev) => {
            const updated = { ...prev, ...newData };
            return updated;
        });
    };

    // Handle phone verification validation in final submit
    const validatePhoneVerification = () => {
        if(!configLoading && phoneVerificationEnabled && !otpVerified) {
            notifyUser(null, "error", "Please verify your phone number before submitting.", "toast", null, null, null, null, null, false, false, "top-center");
            return false;
        }
        return true;
    };

    if(isSubmitted) {
        return (
            <div className="flex flex-col min-h-screen bg-white">
                <TopBar />
                <main className="flex justify-center flex-grow px-2 sm:px-4">
                    <div className="w-full max-w-2xl px-4 pt-8 pb-4 text-center">
                        <h1 className="text-3xl font-bold text-navy mb-4">
                            Application Submitted!
                        </h1>
                        <p className="text-lg text-gray-600">
                            Thank you for submitting your investor application.
                            You will receive a confirmation email shortly.
                        </p>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    const stepComponents = {
        1: (
            <WelcomeStep
                termsAccepted={termsAccepted}
                setTermsAccepted={setTermsAccepted}
                nextStep={nextStep}
            />
        ),
        2: (
            <IdentityVerificationStep
                formData={formData}
                updateFormData={updateFormData}
                nextStep={nextStep}
                prevStep={prevStep}
            />
        ),
        3: (
            <PhoneVerificationStep
                formData={formData}
                updateFormData={updateFormData}
                handlePhoneChange={handlePhoneChange}
                nextStep={nextStep}
                prevStep={prevStep}
                handleSendOtp={handleSendOtp}
                otpLoading={otpLoading}
                otpVerified={otpVerified}
                phoneVerificationEnabled={phoneVerificationEnabled}
                configLoading={configLoading}
            />
        ),
        4: (
            <OTPVerificationStep
                formData={formData}
                updateFormData={updateFormData}
                nextStep={nextStep}
                prevStep={prevStep}
                otp={otp}
                setOtp={setOtp}
                handleVerifyOtp={handleVerifyOtp}
                handleResendOtp={handleResendOtp}
                otpVerified={otpVerified}
                otpLoading={otpLoading}
                otpSent={otpSent}
                phoneVerificationEnabled={phoneVerificationEnabled}
                configLoading={configLoading}
            />
        ),
        5: (
            <InvestmentLimitsStep
                formData={formData}
                updateFormData={updateFormData}
                prevStep={prevStep}
                handleFinalSubmit={handleFinalSubmit}
                isSubmitting={isSubmitting}
            />
        ),
    };

    return (
        <div className="flex flex-col min-h-screen bg-white">
            <TopBar />

            {/* Progress Bar */}
            <div className="bg-gray-100 p-4">
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-navy">
                            Step {getEffectiveStep()} of {getTotalSteps()}
                        </span>
                        <span className="text-sm font-medium text-navy">
                            {Math.round(
                                (getEffectiveStep() / getTotalSteps()) * 100,
                            )}
                            % Complete
                        </span>
                    </div>
                    <div className="w-full bg-gray-300 rounded-full h-2">
                        <div
                            className="bg-navy h-2 rounded-full transition-all duration-300"
                            style={{
                                width: `${
                                    (getEffectiveStep() / getTotalSteps()) * 100
                                }%`,
                            }}
                        ></div>
                    </div>
                </div>
            </div>

            <main className="flex justify-center flex-grow px-2 sm:px-4">
                {stepComponents[currentStep]}
            </main>

            <Footer />
        </div>
    );
}

MultiStepAccount.propTypes = {
    user: PropTypes.object,
    setUser: PropTypes.func.isRequired,
};

export default MultiStepAccount;
